/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CognitoUser } from '@aws-amplify/auth';
import { RootState } from '../../hooks';
// eslint-disable-next-line import/no-cycle
import { deleteAuthThunk } from './authDelete.thunk';
import { ForgotPasswordState, LoggedInUser } from './auth.interfaces';
import { loginThunk } from './login.thunk';
import { currentAuthenticatedUserThunk } from './currentUser.thunk';
import { getAppUserThunk } from './getAppUser.thunk';
import { logoutThunk } from './logout.thunk';
import { syncThunk } from './sync.thunk';
import { User } from '../../reduxRepositories/users/users.interfaces';

interface AuthState {
  cognitoUser: CognitoUser | undefined;
  user: User | undefined | null;
  forgotPasswordEmailConfirmation: ForgotPasswordState;
  forgotPasswordDetailsConfirmation: ForgotPasswordState;
  isSyncCompleted: boolean;
  isSyncing: boolean;
  loading: string;
  errors: any[];
  error: string;
  isAccountDeleted: boolean;
  isNetworkConnected: boolean;
  latestDataStoreError: any;
}

const initialState: AuthState = {
  cognitoUser: undefined,
  user: undefined,
  forgotPasswordEmailConfirmation: {} as ForgotPasswordState,
  forgotPasswordDetailsConfirmation: {} as ForgotPasswordState,
  isSyncCompleted: false,
  isSyncing: false,
  errors: [],
  error: '',
  loading: 'pending',
  isAccountDeleted: false,
  isNetworkConnected: true,
  latestDataStoreError: undefined,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    dispatchIsNetworkConnected: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => {
      state.isNetworkConnected = payload;
      // localStorage.setItem('user', JSON.stringify(payload));
    },
    dispatchDataStoreError: (state, { payload }: PayloadAction<any>) => {
      state.latestDataStoreError = checkDataStoreError(payload);
    },
    dispatchCognitoUser: (state, { payload }: PayloadAction<CognitoUser>) => {
      state.cognitoUser = payload;
      // localStorage.setItem('user', JSON.stringify(payload));
    },
    dispatchUser: (state, { payload }: PayloadAction<User>) => {
      state.user = payload;
      // localStorage.setItem('user', JSON.stringify(payload));
    },
    clearUser: (state) => {
      state.user = undefined;
      // localStorage.removeItem('user');
    },
    setForgotPasswordEmailConfirmation: (state, action: PayloadAction<any>) => {
      state.forgotPasswordEmailConfirmation = action.payload;
    },
    setForgotPasswordDetailsConfirmation: (
      state,
      action: PayloadAction<any>,
    ) => {
      state.forgotPasswordDetailsConfirmation = action.payload;
    },
    setAccountDeleted: (state, action: PayloadAction<boolean>) => {
      state.isAccountDeleted = action.payload;
    },
    updateIsSyncCompleted: (state, action: PayloadAction<boolean>) => {
      state.isSyncCompleted = action.payload;
    },
    updateIsSyncing: (state, action: PayloadAction<boolean>) => {
      state.isSyncing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    });
    builder.addCase(loginThunk.fulfilled, (state, action) => {
      state.loading = 'idle';
      state.errors = [];
      state.error = '';
      // console.log('login: ', action.payload);
      state.cognitoUser = action.payload;
    });
    builder.addCase(
      loginThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.user = null;
        state.errors.push(action.payload.message);
        state.error = findMessageText(action.payload.message);
        state.loading = 'idle';
      },
    );

    builder.addCase(currentAuthenticatedUserThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    });
    builder.addCase(
      currentAuthenticatedUserThunk.fulfilled,
      (state, action) => {
        state.loading = 'idle';
        state.errors = [];
        state.error = '';
        // TODO: figure out why error is shown in browser when thunk throws
        if (!action.payload) return;

        state.cognitoUser = action.payload;
      },
    );
    // This is needed to prevent flashing the logout screen when you refresh the page.
    // It distinguishes a null user (discovered to be invalid/expired) from an undefined one (either not yet checked, or deliberately reset)
    builder.addCase(
      currentAuthenticatedUserThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.user = null;
        // state.errors = action.payload;
        state.loading = 'idle';
      },
    );

    builder.addCase(logoutThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    });
    builder.addCase(logoutThunk.fulfilled, (state, action) => {
      state.loading = 'idle';
      state.errors = [];
      state.error = '';
    });
    builder.addCase(
      logoutThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.errors.push(action.payload.message);
        state.error = findMessageText(action.payload.message);
        state.loading = 'idle';
      },
    );

    builder.addCase(getAppUserThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    });
    builder.addCase(getAppUserThunk.fulfilled, (state, action) => {
      state.loading = 'idle';
      state.errors = [];
      state.error = '';
      // console.log('appUser fulfilled: ', action.payload);
      state.user = action.payload;
    });
    builder.addCase(
      getAppUserThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.error = findMessageText(action.payload.message);
        state.errors.push(action.payload.message);
        state.loading = 'idle';
      },
    );

    builder.addCase(syncThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    });
    builder.addCase(syncThunk.fulfilled, (state, action) => {
      state.loading = 'idle';
      state.error = '';
      state.errors = [];
      // console.log('isSyncCompleted: ', action.payload);
      state.isSyncCompleted = action.payload;
    });
    builder.addCase(syncThunk.rejected, (state, action: PayloadAction<any>) => {
      state.error = findMessageText(action.payload.message);
      state.errors.push(action.payload.message);
      state.loading = 'idle';
    });

    builder.addCase(deleteAuthThunk.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
      state.isAccountDeleted = false;
    });
    builder.addCase(deleteAuthThunk.fulfilled, (state, action) => {
      state.loading = 'idle';
      state.isAccountDeleted = true;
      state.error = '';
      state.errors = [];
    });
    builder.addCase(
      deleteAuthThunk.rejected,
      (state, action: PayloadAction<any>) => {
        state.error = findMessageText(action.payload.message);
        state.errors.push(action.payload.message);
        state.loading = 'idle';
        state.isAccountDeleted = false;
      },
    );
  },
});

export const cognitoUserSelector = (
  state: RootState,
): CognitoUser | undefined => {
  return state.authReducer.cognitoUser;
};

export const loggedInUserSelector = (state: RootState): LoggedInUser => {
  return state.authReducer.user;
};

export const forgotPasswordEmailConfirmation = (state: RootState): any => {
  return state.authReducer.forgotPasswordEmailConfirmation;
};

export const isSyncCompletedSelector = (state: RootState): boolean => {
  return state.authReducer.isSyncCompleted;
};

export const isSyncingSelector = (state: RootState): boolean => {
  return state.authReducer.isSyncing;
};

export const forgotPasswordDetailsConfirmation = (state: RootState): any => {
  return state.authReducer.forgotPasswordDetailsConfirmation;
};

export const authErrorsSelector = (state: RootState): string[] => {
  return state.authReducer.errors;
};
export const authLastErrorSelector = (state: RootState): string => {
  return state.authReducer.error;
};

export const isAccountDeletedSelector = (state: RootState): any => {
  return state.authReducer.isAccountDeleted;
};

export const checkIfIsNetworkConnected = (state: RootState): any => {
  return state.authReducer.isNetworkConnected;
};

export const dataStoreErrorSelector = (state: RootState): any => {
  return state.authReducer.latestDataStoreError;
};

export const isLoadingSelector = (state: RootState): boolean =>
  state.authReducer.loading !== 'idle';

const findMessageText = (messageObject: any) => {
  if (typeof messageObject === 'string') return messageObject;
  if (messageObject.errors && Array.isArray(messageObject.errors)) {
    const firstError = messageObject.errors[0];
    if (firstError && firstError.message) return firstError.message;
  }
  return messageObject.toString();
};

const checkDataStoreError = (sourceMessage: string) => {
  if (sourceMessage === 'Request failed with status code 403') {
    return 'ACCESS_FORBIDDEN';
  }
  return sourceMessage;
};

// Action creators are generated for each case reducer function
export const {
  dispatchCognitoUser,
  dispatchUser,
  clearUser,
  setForgotPasswordEmailConfirmation,
  setForgotPasswordDetailsConfirmation,
  updateIsSyncCompleted,
  updateIsSyncing,
  setAccountDeleted,
  dispatchIsNetworkConnected,
  dispatchDataStoreError,
} = authSlice.actions;
